import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import { formatKabulDisplay } from "./ScheduleFields";
import type { StatusTone } from "../lib/overview-ops";
import {
  relatedVersionsLinks,
  shortChecksum,
  versionIntegrityDisplay,
  versionKindDisplay,
  versionLifecycleDisplay,
  type VersionSnapshotRow,
  type VersionSortKey,
} from "../lib/versions-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: VersionSortKey;
  onSortChange: (value: VersionSortKey) => void;
  organizationLabel: string;
  total: number;
  limit: number;
  offset: number;
};

export function VersionsFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  organizationLabel,
  total,
  limit,
  offset,
}: FilterBarProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "promotion", label: "پیشنهادها" },
    { id: "announcement", label: "اطلاعیه‌ها" },
    { id: "sponsored", label: "تبلیغات ویژه" },
    { id: "publish", label: "انتشار" },
    { id: "rollback", label: "بازگردانی" },
  ];
  const rangeEnd = Math.min(offset + limit, total);
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر تاریخچه انتشار"
    >
      <p className="sites-inventory__muted" style={{ width: "100%" }}>
        دامنه سازمان فعال:{" "}
        <strong>
          <bdi dir="ltr" className="ltr-isolate latin-digits">
            {organizationLabel}
          </bdi>
        </strong>
      </p>
      <p className="sites-inventory__muted" style={{ width: "100%" }}>
        صفحه API:{" "}
        <bdi dir="ltr" className="ltr-isolate latin-digits">
          {total === 0 ? "0" : `${offset + 1}–${rangeEnd}`}
        </bdi>
        {" از "}
        <bdi dir="ltr" className="ltr-isolate latin-digits">
          {total}
        </bdi>
        {" (limit "}
        <bdi dir="ltr" className="ltr-isolate latin-digits">
          {limit}
        </bdi>
        )
      </p>
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر تاریخچه انتشار"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجو</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="عنوان، نوع، نسخه، منتشرکننده…"
          aria-label="جستجوی تاریخچه انتشار"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as VersionSortKey)}
          aria-label="مرتب‌سازی تاریخچه انتشار"
        >
          <option value="time">زمان</option>
          <option value="version">نسخه</option>
          <option value="kind">نوع</option>
          <option value="title">عنوان</option>
          <option value="actor">منتشرکننده</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function VersionInspectSummary({
  item,
  organizationLabel,
  actions,
}: {
  item: VersionSnapshotRow;
  organizationLabel: string;
  actions?: ReactNode;
}) {
  const kind = versionKindDisplay(item.kind);
  const lifecycle = versionLifecycleDisplay(item);
  const integrity = versionIntegrityDisplay(item);
  const links = relatedVersionsLinks(item.kind);

  return (
    <section className="content-inspect" aria-labelledby="version-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">تاریخچه انتشار · نسخه محتوا</p>
          <h2 id="version-inspect-title" className="content-inspect__name">
            {item.title}
          </h2>
          <p className="content-inspect__id">
            نسخه{" "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.version}
            </bdi>
            {" · اسنپ‌شات "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.id}
            </bdi>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="نوع و یکپارچگی">
          <span className={`state-badge ${TONE_BADGE[kind.tone]}`}>{kind.label}</span>
          <span className={`state-badge ${TONE_BADGE[lifecycle.tone]}`}>{lifecycle.label}</span>
          <span className={`state-badge ${TONE_BADGE[integrity.tone]}`}>{integrity.label}</span>
        </div>
      </header>

      {lifecycle.detail && (
        <p className="content-inspect__eligibility-detail">{lifecycle.detail}</p>
      )}
      {integrity.detail && (
        <p className="content-inspect__eligibility-detail">{integrity.detail}</p>
      )}

      <dl className="content-inspect__facts">
        <div>
          <dt>سازمان فعال</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {organizationLabel}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>نوع محتوا</dt>
          <dd>
            {kind.label}{" "}
            (
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.kind}
            </bdi>
            )
          </dd>
        </div>
        <div>
          <dt>شناسه محتوا</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.contentItemId}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>منتشرکننده</dt>
          <dd>{item.publishedBy}</dd>
        </div>
        <div>
          <dt>زمان انتشار</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.publishedAt)}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>checksum</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              <code>{item.payloadChecksum || "—"}</code>
            </bdi>
          </dd>
        </div>
        {item.rollbackOfSnapshotId && (
          <div>
            <dt>منبع بازگردانی</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.rollbackOfSnapshotId}
              </bdi>
            </dd>
          </div>
        )}
      </dl>

      <nav className="content-inspect__links" aria-label="مسیرهای مرتبط">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

export function VersionsInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: {
  rows: VersionSnapshotRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: VersionSnapshotRow) => ReactNode;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول تاریخچه انتشار"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">عنوان</th>
              <th scope="col">نوع</th>
              <th scope="col">نسخه</th>
              <th scope="col">چرخه</th>
              <th scope="col">یکپارچگی</th>
              <th scope="col">منتشرکننده</th>
              <th scope="col">زمان</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const kind = versionKindDisplay(row.kind);
              const lifecycle = versionLifecycleDisplay(row);
              const integrity = versionIntegrityDisplay(row);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      {row.title}
                    </button>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[kind.tone]}`}>{kind.label}</span>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.kind}
                    >
                      <LtrIsolate mono>{row.kind}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={String(row.version)}>
                      <LtrIsolate mono>{row.version}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[lifecycle.tone]}`}>
                      {lifecycle.label}
                    </span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[integrity.tone]}`}>
                      {integrity.label}
                    </span>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.payloadChecksum || undefined}
                    >
                      <LtrIsolate mono>
                        <code>{shortChecksum(row.payloadChecksum)}</code>
                      </LtrIsolate>
                    </div>
                  </td>
                  <td>{row.publishedBy}</td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.publishedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.publishedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های تاریخچه انتشار"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const kind = versionKindDisplay(row.kind);
          const lifecycle = versionLifecycleDisplay(row);
          const integrity = versionIntegrityDisplay(row);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">{row.title}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[kind.tone]}`}>{kind.label}</span>
                  <span className={`state-badge ${TONE_BADGE[lifecycle.tone]}`}>
                    {lifecycle.label}
                  </span>
                  <span className={`state-badge ${TONE_BADGE[integrity.tone]}`}>
                    {integrity.label}
                  </span>
                </span>
                <span className="sites-inventory__muted">
                  نسخه{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.version}
                  </bdi>
                  {" · "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.publishedAt)}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    <code>{shortChecksum(row.payloadChecksum)}</code>
                  </bdi>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
